import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { getProductRef } from '../../api/refApi';
import { useAllCategories, useCreateProductSet, useUpdateProductSet } from '../../hooks';
import type { ToastSeverity } from '../../lib/useToast';
import type { ProductRef, ProductSetKind, ProductSetResponse } from '../../model/types';
import { ChipListInput } from './ChipListInput';
import { ProductAutocomplete } from './ProductAutocomplete';

export interface ProductSetEditorDrawerProps {
  open: boolean;
  editing?: ProductSetResponse;
  onClose: () => void;
  onSaved: (set: ProductSetResponse) => void;
  onResult: (severity: ToastSeverity, message: string) => void;
}

interface DraftState {
  code: string;
  name: string;
  description: string;
  kind: ProductSetKind;
  products: ProductRef[];
  exclusions: ProductRef[];
  categoryIds: string[];
  brandCodes: string[];
  minPrice: string;
  maxPrice: string;
}

const EMPTY_DRAFT: DraftState = {
  code: '',
  name: '',
  description: '',
  kind: 'STATIC',
  products: [],
  exclusions: [],
  categoryIds: [],
  brandCodes: [],
  minPrice: '',
  maxPrice: '',
};

function seedDraft(editing: ProductSetResponse | undefined): DraftState {
  if (!editing) return EMPTY_DRAFT;
  const includedEntries = editing.entries.filter(
    (e) => e.memberKind === 'PRODUCT' && e.inclusion !== 'EXCLUDE',
  );
  const excludedEntries = editing.entries.filter(
    (e) => e.memberKind === 'PRODUCT' && e.inclusion === 'EXCLUDE',
  );
  return {
    code: editing.code,
    name: editing.name,
    description: editing.description ?? '',
    kind: editing.kind,
    products: includedEntries.map((e) => ({ id: e.referenceId ?? '', name: e.referenceCode ?? e.referenceId ?? '' })),
    exclusions: excludedEntries.map((e) => ({ id: e.referenceId ?? '', name: e.referenceCode ?? e.referenceId ?? '' })),
    categoryIds: editing.filterParams?.categoryIds ?? [],
    brandCodes: editing.filterParams?.brandCodes ?? [],
    minPrice: editing.filterParams?.minPrice != null ? String(editing.filterParams.minPrice) : '',
    maxPrice: editing.filterParams?.maxPrice != null ? String(editing.filterParams.maxPrice) : '',
  };
}

/**
 * Right-side drawer to create/edit a promo product set (plan-011 §8.10) — the house
 * `PriceCellEditorDrawer` layout pattern. STATIC sets are built entirely by name (no UUID ever
 * shown); FILTER sets support category/brand/price-range only (adr-pricing-016).
 */
export function ProductSetEditorDrawer({
  open,
  editing,
  onClose,
  onSaved,
  onResult,
}: ProductSetEditorDrawerProps) {
  const { t } = useTranslation('promotions');
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [showExclusions, setShowExclusions] = useState(false);
  const categories = useAllCategories({ enabled: open });
  const createSet = useCreateProductSet();
  const updateSet = useUpdateProductSet();
  const pending = createSet.isPending || updateSet.isPending;

  useEffect(() => {
    if (!open) return;
    const seeded = seedDraft(editing);
    setDraft(seeded);
    setShowExclusions(Boolean(editing) && seeded.exclusions.length > 0);

    // Entries only carry `referenceId`/`referenceCode` (never a resolved name) — back-fill real
    // product names so the Autocomplete never has to show a raw UUID chip (plan-011's no-UUID
    // rule). Best-effort: a failed/deleted lookup just keeps the id/code fallback already seeded.
    if (editing && editing.kind === 'STATIC') {
      const needsName = (p: ProductRef) => p.name === p.id; // seedDraft falls back to id when no code
      const toResolve = [...seeded.products, ...seeded.exclusions].filter(needsName);
      if (toResolve.length > 0) {
        void Promise.allSettled(toResolve.map((p) => getProductRef(p.id))).then((results) => {
          const resolved = new Map<string, ProductRef>();
          results.forEach((result) => {
            if (result.status === 'fulfilled' && result.value) resolved.set(result.value.id, result.value);
          });
          if (resolved.size === 0) return;
          setDraft((d) => ({
            ...d,
            products: d.products.map((p) => resolved.get(p.id) ?? p),
            exclusions: d.exclusions.map((p) => resolved.get(p.id) ?? p),
          }));
        });
      }
    }
  }, [open, editing]);

  const isValid =
    draft.code.trim().length > 0 &&
    draft.name.trim().length > 0 &&
    (draft.kind === 'STATIC' ? draft.products.length > 0 : true);

  const handleSave = async () => {
    const body = {
      code: draft.code.trim(),
      name: draft.name.trim(),
      description: draft.description.trim() || undefined,
      kind: draft.kind,
      ...(draft.kind === 'STATIC'
        ? {
            entries: [
              ...draft.products.map((p) => ({
                memberKind: 'PRODUCT' as const,
                inclusion: 'INCLUDE' as const,
                referenceId: p.id,
              })),
              ...draft.exclusions.map((p) => ({
                memberKind: 'PRODUCT' as const,
                inclusion: 'EXCLUDE' as const,
                referenceId: p.id,
              })),
            ],
          }
        : {
            filterParams: {
              categoryIds: draft.categoryIds.length ? draft.categoryIds : undefined,
              brandCodes: draft.brandCodes.length ? draft.brandCodes : undefined,
              minPrice: draft.minPrice.trim() ? Number(draft.minPrice) : undefined,
              maxPrice: draft.maxPrice.trim() ? Number(draft.maxPrice) : undefined,
            },
          }),
    };

    try {
      const saved = editing
        ? await updateSet.mutateAsync({ productSetId: editing.id, body })
        : await createSet.mutateAsync(body);
      onResult('success', t('productSetEditorDrawer.saved'));
      onSaved(saved);
      onClose();
    } catch (err) {
      onResult(
        'error',
        (err as { message?: string }).message ?? t('productSetEditorDrawer.saveError'),
      );
    }
  };

  const categoryOptions = categories.data ?? [];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 480 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {editing ? t('productSetEditorDrawer.editTitle') : t('productSetEditorDrawer.newTitle')}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        <Stack spacing={2.5}>
          <TextField
            label={t('productSetEditorDrawer.nameLabel')}
            required
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            fullWidth
          />
          <TextField
            label={t('productSetEditorDrawer.codeLabel')}
            required
            disabled={Boolean(editing)}
            value={draft.code}
            onChange={(e) => setDraft((d) => ({ ...d, code: e.target.value }))}
            fullWidth
          />

          <ToggleButtonGroup
            exclusive
            value={draft.kind}
            disabled={Boolean(editing)}
            onChange={(_e, next: ProductSetKind | null) => {
              if (next) setDraft((d) => ({ ...d, kind: next }));
            }}
            size="small"
          >
            <ToggleButton value="STATIC">{t('productSetEditorDrawer.kindStatic')}</ToggleButton>
            <ToggleButton value="FILTER">{t('productSetEditorDrawer.kindFilter')}</ToggleButton>
          </ToggleButtonGroup>

          <Divider />

          {draft.kind === 'STATIC' ? (
            <>
              <ProductAutocomplete
                label={t('productSetEditorDrawer.productsLabel')}
                value={draft.products}
                onChange={(products) => setDraft((d) => ({ ...d, products }))}
              />
              {showExclusions ? (
                <ProductAutocomplete
                  label={t('productSetEditorDrawer.exclusionsLabel')}
                  value={draft.exclusions}
                  onChange={(exclusions) => setDraft((d) => ({ ...d, exclusions }))}
                />
              ) : (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowExclusions(true)}
                  sx={{ alignSelf: 'flex-start', pl: 0 }}
                >
                  {t('productSetEditorDrawer.addExclusions')}
                </Button>
              )}
            </>
          ) : (
            <>
              <Autocomplete
                multiple
                options={categoryOptions}
                loading={categories.isFetching}
                value={categoryOptions.filter((c) => draft.categoryIds.includes(c.id))}
                getOptionLabel={(c) => c.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_e, next) => setDraft((d) => ({ ...d, categoryIds: next.map((c) => c.id) }))}
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return <Chip size="small" label={tag.name} key={key} {...tagProps} />;
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label={t('productSetEditorDrawer.categoriesLabel')} />
                )}
              />
              <ChipListInput
                label={t('productSetEditorDrawer.brandsLabel')}
                value={draft.brandCodes}
                onChange={(brandCodes) => setDraft((d) => ({ ...d, brandCodes }))}
                placeholder={t('productSetEditorDrawer.brandsPlaceholder')}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label={t('productSetEditorDrawer.minPriceLabel')}
                  type="number"
                  value={draft.minPrice}
                  onChange={(e) => setDraft((d) => ({ ...d, minPrice: e.target.value }))}
                  slotProps={{ htmlInput: { min: 0 } }}
                  fullWidth
                />
                <TextField
                  label={t('productSetEditorDrawer.maxPriceLabel')}
                  type="number"
                  value={draft.maxPrice}
                  onChange={(e) => setDraft((d) => ({ ...d, maxPrice: e.target.value }))}
                  slotProps={{ htmlInput: { min: 0 } }}
                  fullWidth
                />
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('productSetEditorDrawer.filterMaterializeHint')}
              </Typography>
            </>
          )}
        </Stack>
      </Box>

      <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button onClick={onClose} disabled={pending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!isValid || pending}
          startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('common:action.save')}
        </Button>
      </Box>
    </Drawer>
  );
}
